import { T, useGT } from 'gt-react';
import { DownloadSimpleIcon, TrashIcon } from '@phosphor-icons/react';

import Page from 'src/components/page/Page';
import { Button } from '@/components/ui/button';
import {
  EATON_DSP_ACCEPT,
  validateEatonDspFile,
} from '@/lib/eaton-dsp/excelFile';
import { FileDropzone } from '../../components/FileDropzone';
import {
  DuplicatesTable,
  ExtractSummary,
  FileResultList,
  MissingSpasTable,
} from './EatonDspReadout';
import { useEatonDsp } from './useEatonDsp';

/**
 * Item Master → Utilities → Eaton DSP Extract: flatten one tab per SPA
 * (or an already-flat WJ sheet) into a single workbook with coverage and
 * duplicate catalogs.
 */
export function EatonDspPage() {
  const gt = useGT();
  const {
    results,
    summary,
    isExtracting,
    addFiles,
    removeFile,
    clear,
    downloadExtract,
  } = useEatonDsp();

  return (
    <Page
      title={
        <T context="page title for the Eaton DSP extraction utility">
          Eaton DSP Extract
        </T>
      }
      subtitle={
        <T context="subtitle for the Eaton DSP extraction utility">
          Drop an Eaton Westburne master list or a WJ DSP workbook. Every SPA
          tab is copied into one sheet, stamped with the SPA id, region, and the
          branches that SPA covers.
        </T>
      }
      actions={
        results.length > 0 ? (
          <div className="flex gap-2">
            {summary.priceCount > 0 ? (
              <Button onClick={downloadExtract} disabled={isExtracting}>
                <DownloadSimpleIcon className="h-4 w-4" />
                <T context="button that downloads the flattened Eaton DSP workbook">
                  Download Extract
                </T>
              </Button>
            ) : null}
            <Button variant="outline" onClick={clear} disabled={isExtracting}>
              <TrashIcon className="h-4 w-4" />
              <T context="button that clears all dropped Eaton DSP files">
                Clear Files
              </T>
            </Button>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col gap-4 pt-4">
        <FileDropzone
          multiple
          accept={EATON_DSP_ACCEPT}
          disabled={isExtracting}
          prompt={
            <T context="instruction inside the Eaton DSP drag-and-drop area">
              Drag and drop .xls, .xlsx, or .xlsb Eaton DSP workbooks here.
            </T>
          }
          validate={(file) => Promise.resolve(validateEatonDspFile(file, gt))}
          onFilesAccepted={(files) => {
            void addFiles(files);
          }}
        />
        <FileResultList
          results={results}
          onRemove={removeFile}
          isExtracting={isExtracting}
        />
        <ExtractSummary summary={summary} />
        <DuplicatesTable rows={summary.duplicates} />
        <MissingSpasTable rows={summary.missingSpas} />
      </div>
    </Page>
  );
}
